/**
 * 套餐选择状态的本地持久化（T14）。
 *
 * 存储介质：localStorage，键固定为 `t14.plan`。
 * 只存「模式选择」这一件事，且官方套餐与自行配置两种模式互斥：
 *
 *   - `{ mode: 'official', plan: 'text' | 'multimodal' | 'voice' }` 选了一个官方套餐；
 *   - `{ mode: 'byo', plan: null }` 不使用官方订阅，自行配置文字 / 生图 / TTS 模型；
 *   - `{ mode: null, plan: null }` 还没做选择（首次进入）。
 *
 * 读取时统一做一次归一化，外部写入的脏数据不会破坏下游调用。
 */

import { isPlanTier } from './plan';
import type { PlanTier } from './plan';

/** 需求约定的存储键。 */
export const PLAN_STORAGE_KEY = 't14.plan';

/** 两种互斥的订阅模式；`null` 表示尚未选择。 */
export type SubscriptionMode = 'official' | 'byo';

/** 订阅选择状态。 */
export interface SubscriptionSelection {
  mode: SubscriptionMode | null;
  /** 仅当 `mode === 'official'` 时非空。 */
  plan: PlanTier | null;
}

/** 未做任何选择时的状态。 */
export const UNSELECTED: SubscriptionSelection = { mode: null, plan: null };

/** 判断两个选择是否等价（用于去重写入与状态比对）。 */
export function sameSelection(a: SubscriptionSelection, b: SubscriptionSelection): boolean {
  return a.mode === b.mode && a.plan === b.plan;
}

/** 归一化任意输入为合法的选择状态；缺失 / 非法一律回落到「未选择」。 */
export function normalizeSelection(input: unknown): SubscriptionSelection {
  if (input === null || typeof input !== 'object') return { ...UNSELECTED };

  const raw = input as { mode?: unknown; plan?: unknown };

  if (raw.mode === 'byo') {
    return { mode: 'byo', plan: null };
  }

  if (raw.mode === 'official' && isPlanTier(raw.plan)) {
    return { mode: 'official', plan: raw.plan };
  }

  // official 但档位非法 / mode 缺失或未知：一律视为未选择。
  return { ...UNSELECTED };
}

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** 读取选择；未写入过或数据损坏时返回「未选择」。 */
export function loadSelection(): SubscriptionSelection {
  const store = storage();
  if (!store) return { ...UNSELECTED };

  const raw = store.getItem(PLAN_STORAGE_KEY);
  if (raw === null) return { ...UNSELECTED };

  try {
    return normalizeSelection(JSON.parse(raw));
  } catch {
    return { ...UNSELECTED };
  }
}

/** 覆盖写入选择，返回归一化后的实际值。 */
export function saveSelection(selection: SubscriptionSelection): SubscriptionSelection {
  const normalized = normalizeSelection(selection);
  storage()?.setItem(PLAN_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

/** 选择官方套餐：自动关闭「自行配置」模式（两模式互斥）。 */
export function selectPlan(tier: PlanTier): SubscriptionSelection {
  return saveSelection({ mode: 'official', plan: tier });
}

/** 选择「不使用官方订阅，自行配置模型」：清除官方套餐。 */
export function selectByo(): SubscriptionSelection {
  return saveSelection({ mode: 'byo', plan: null });
}

/** 清空选择，回到「未选择」。 */
export function clearSelection(): SubscriptionSelection {
  return saveSelection({ ...UNSELECTED });
}

/** 是否已经做出互斥选择（官方套餐或自行配置）。 */
export function hasSelection(selection: SubscriptionSelection): boolean {
  return selection.mode === 'official' && selection.plan !== null
    ? true
    : selection.mode === 'byo';
}

/** 当前选择的一句中文说明，直接给设置页 / 选择页复用。 */
export function describeSelection(
  selection: SubscriptionSelection,
  nameOfPlan: (tier: PlanTier) => string,
): string {
  if (selection.mode === 'official' && selection.plan !== null) {
    return `官方订阅：${nameOfPlan(selection.plan)}`;
  }
  if (selection.mode === 'byo') {
    return '自行配置文字 / 生图 / TTS 模型';
  }
  return '尚未选择套餐';
}